import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Grid, Paper, useMediaQuery, useTheme, Drawer, IconButton, Fab, Backdrop, CircularProgress } from '@mui/material';
import { 
  CameraAlt, 
  Palette, 
  SportsSoccer, 
  ArrowForward,
  ExpandMore,
  ChevronRight,
  FlipCameraAndroid,
  Image,
  Badge,
  ShoppingCart,
  Close,
  CheckCircle
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

// Import modal components
import ColorPickerModal from '../components/ColorPickerModal';
import OrderFormModal from '../components/OrderFormModal';
import KitPreview from '../components/KitPreview';
import { publicUploadUrl } from '../utils/mediaUrls';
import { KIT_COLOR_SWATCHES, getKitColorName, normalizeHex, isLightSwatchHex } from '../constants/kitColorPalette';
import { calculateQuotePricing } from '../utils/quotePricing';
import { formatMoney } from '../utils/currency';

const FootballDesign = () => {
  const { slug: category } = useParams() || { slug: 'football' }; // Get category from URL, default to 'football'
  const actualCategory = category || 'football'; // Fallback to 'football' if not provided
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [activeColorTab, setActiveColorTab] = useState('color1');
  const [selectedKit, setSelectedKit] = useState(null); // Will be set when designs are loaded
  const [selectedKitPrice, setSelectedKitPrice] = useState(29.99); // Keep for backward compatibility
  const [selectedYoungKitPrice, setSelectedYoungKitPrice] = useState(29.99);
  const [selectedAdultKitPrice, setSelectedAdultKitPrice] = useState(29.99);
  const [currentView, setCurrentView] = useState('front'); // 'front' or 'back'
  const [selectedColors, setSelectedColors] = useState({
    color1: '#ffffff', // White - Primary color (original SVG color)
    color2: '#070707', // Very dark gray/black - Secondary color (original SVG color)
    color3: '#ff0000', // Red - Accent color (original SVG color)
    color4: '#ff6500', // Orange - Orange areas
    color5: '#636466', // Dark Grey - Dark areas
    logoColor: '#ec0303', // Logo red (original SVG color)
    numberColor: '#dfc200' // Yellow/gold - Number color (original SVG color)
  });

  // Order form state
  const [orderFormData, setOrderFormData] = useState({
    name: '',
    email: '',
    phone: '',
    teamName: '',
    kitType: 'adult',
    quantity: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState('');
  
  // Helper function to get color name
  const getColorName = (hexColor) => getKitColorName(hexColor, 'Unknown');
  
  // Modal states
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [activeColorPicker, setActiveColorPicker] = useState('color1');
  
  // Bottom sheet states for mobile
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [activeBottomSheet, setActiveBottomSheet] = useState(null); // 'design', 'colors', 'logo', 'order'

  // Kit designs data - now dynamic from API
  const [kitDesigns, setKitDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [frontSvgData, setFrontSvgData] = useState(null);
  const [backSvgData, setBackSvgData] = useState(null);
  const [svgLoading, setSvgLoading] = useState(false);

  const tabs = [
    { label: 'Design', value: 0 },
    { label: 'Colours', value: 1 },
    { label: 'Logo & Number', value: 2 },
    { label: 'Quote', value: 3 }
  ];

  // Form validation function
  const validateForm = () => {
    const errors = {};
    
    if (!orderFormData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!orderFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(orderFormData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!orderFormData.phone.trim()) {
      errors.phone = 'Phone is required';
    }
    
    if (!orderFormData.teamName.trim()) {
      errors.teamName = 'Team name is required';
    }
    
    if (!orderFormData.quantity) {
      errors.quantity = 'Quantity is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form input changes
  const handleFormChange = (field, value) => {
    setOrderFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle form submission
  const handleFormSubmit = async () => {
    if (!validateForm()) {
      return false; // Return false if validation fails
    }
    
    setIsSubmitting(true);
    setSubmissionStatus('Preparing your design with applied colors...');

    try {
      // Generate an order number locally for tracking this submission
      const orderNumber = `ZAR-${Date.now()}`;
      const teamName = orderFormData.teamName || '';
      // Prepare the quote data
      const quoteData = {
        customer_name: orderFormData.name,
        customer_email: orderFormData.email,
        customer_phone: orderFormData.phone,
        team_name: orderFormData.teamName,
        quantity: orderFormData.quantity,
        size: orderFormData.size,
        address: orderFormData.address,
        city: orderFormData.city,
        postal_code: orderFormData.postalCode,
        country: orderFormData.country,
        selected_kit_id: selectedKit || null,
        selected_colors: selectedColors,
        front_svg_data: frontSvgData,
        back_svg_data: backSvgData
      };


      const apiUrl = process.env.REACT_APP_API_URL || "/api";

      // Step 1: Upload colored SVGs as multipart (streamed)
      setSubmissionStatus('Capturing your custom colors and design...');
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay for better UX
      
      // Prefer the on-screen, color-applied SVG DOM if available
      const frontEl = document.querySelector('#kit-front-holder svg');
      const backEl  = document.querySelector('#kit-back-holder svg');
      const serializer = new XMLSerializer();
      const frontSerialized = frontEl ? serializer.serializeToString(frontEl) : frontSvgData;
      const backSerialized  = backEl  ? serializer.serializeToString(backEl)  : backSvgData;

      const formData = new FormData();
      if (frontSerialized) {
        const frontBlob = new Blob([frontSerialized], { type: 'image/svg+xml' });
        formData.append('front_svg', frontBlob, `front-${orderNumber}.svg`);
      }
      if (backSerialized) {
        const backBlob = new Blob([backSerialized], { type: 'image/svg+xml' });
        formData.append('back_svg', backBlob, `back-${orderNumber}.svg`);
      }
      formData.append('upload_id', orderNumber);
      formData.append('meta', JSON.stringify({ selected_colors: selectedColors, selected_kit_id: selectedKit, team_name: teamName, quantity: orderFormData.quantity }));

      setSubmissionStatus('Uploading your design files...');
      const uploadResp = await fetch(`${apiUrl}/quote-assets`, { method: 'POST', body: formData });
      if (!uploadResp.ok) {
        const t = await uploadResp.text();
        throw new Error(`SVG upload failed: ${t}`);
      }
      const uploadJson = await uploadResp.json();

      // Step 2: Submit quote with URLs
      setSubmissionStatus('Processing your order details...');
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay for better UX
      
      const pricing = calculateQuotePricing({
        quantity: orderFormData.quantity,
        youngKitPrice: selectedYoungKitPrice,
        adultKitPrice: selectedAdultKitPrice,
        kitType: orderFormData.kitType,
        size: orderFormData.size,
        fallbackPrice: selectedAdultKitPrice || selectedYoungKitPrice || 29.99
      });

      const finalQuoteData = {
        ...quoteData,
        kit_type: pricing.kit_type,
        total_amount: pricing.total_amount,
        kit_price: pricing.kit_price,
        young_kit_price: pricing.young_kit_price,
        adult_kit_price: pricing.adult_kit_price,
        young_kit_total: pricing.young_kit_total,
        adult_kit_total: pricing.adult_kit_total,
        front_svg_url: uploadJson.front_svg_url || null,
        back_svg_url: uploadJson.back_svg_url || null,
        front_svg_data: undefined,
        back_svg_data: undefined
      };

      setSubmissionStatus('Finalizing your order...');
      const response = await axios.post(`${apiUrl}/quotes`, finalQuoteData);
    
      if (response.data.success) {
        setSubmissionStatus('Order submitted successfully! Redirecting...');
        // Show success message
        const orderNo = response.data.order_number || orderNumber;
        
        // Small delay to show success message
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Navigate to Thank You page with order number
        navigate('/thank-you', {
          state: {
            orderNumber: orderNo,
            pricing,
            teamName: orderFormData.teamName,
            designName: kitDesigns.find((k) => k.id === selectedKit)?.name
          }
        });
      
        // Reset form
        setOrderFormData({
          name: '',
          email: '',
          phone: '',
          teamName: '',
          kitType: 'adult',
          quantity: '',
          size: '',
          address: '',
          city: '',
          postalCode: '',
          country: ''
        });
      
        // Clear form errors
        setFormErrors({});
        return true; // Return true on successful submission
      } else {
        throw new Error(response.data.message || 'Failed to submit quote');
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      setIsSubmitting(false);
      setSubmissionStatus('');
      
      // Extract error message from response
      let errorMessage = 'Error submitting quote. Please try again.';
      if (error.response && error.response.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`Error: ${errorMessage}`);
      return false; // Return false on error
    }
  };

  // Fetch kit designs from API
  useEffect(() => {
    const fetchKitDesigns = async () => {
      setLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "/api";
        
        const response = await axios.get(`${apiUrl}/posts/category/${actualCategory}`);
        
        // Transform API data to match component expectations
        const transformedDesigns = response.data.map((post, index) => {
          // Try to use the actual featured image with multiple encoding attempts
          let imageUrl = 'https://via.placeholder.com/300x300?text=No+Image';
          
          if (post.featured_image_name) {
            imageUrl = publicUploadUrl(post.featured_image_name);
          }
          
          return {
            id: post.id,
            name: post.title || `Design ${index + 1}`,
            image: imageUrl,
            frontSvgId: post.front_svg_id,
            backSvgId: post.back_svg_id,
            price: parseFloat(post.price) || 29.99, // Keep for backward compatibility
            youngKitPrice: parseFloat(post.young_kit_price) || parseFloat(post.price) || 29.99,
            adultKitPrice: parseFloat(post.adult_kit_price) || parseFloat(post.price) || 29.99
          };
        });
        
        setKitDesigns(transformedDesigns);
        
        // Reset SVG data when category changes
        setFrontSvgData(null);
        setBackSvgData(null);
        
        // Set first design as selected if available
        if (transformedDesigns.length > 0) {
          const firstDesign = transformedDesigns[0];
          setSelectedKit(firstDesign.id);
          setSelectedKitPrice(parseFloat(firstDesign.price) || 29.99); // Keep for backward compatibility
          setSelectedYoungKitPrice(parseFloat(firstDesign.youngKitPrice) || parseFloat(firstDesign.price) || 29.99);
          setSelectedAdultKitPrice(parseFloat(firstDesign.adultKitPrice) || parseFloat(firstDesign.price) || 29.99);
          
          // Load SVG data for the first design
          if (firstDesign.frontSvgId || firstDesign.backSvgId) {
            setSvgLoading(true);
            
            // Create promises for both SVGs (handle cases where IDs might be null)
            const frontPromise = firstDesign.frontSvgId ? 
              fetchSvgData(firstDesign.frontSvgId, 'front') : 
              Promise.resolve(null);
            
            const backPromise = firstDesign.backSvgId ? 
              fetchSvgData(firstDesign.backSvgId, 'back') : 
              Promise.resolve(null);
            
            // Wait for both promises to complete
            Promise.all([frontPromise, backPromise]).then(([frontSvg, backSvg]) => {
              setFrontSvgData(frontSvg);
              setBackSvgData(backSvg);
              setSvgLoading(false);
            }).catch(error => {
              console.error('Error loading SVG data:', error);
              setSvgLoading(false);
            });
          } else {
            // No SVG IDs available, set loading to false
            setSvgLoading(false);
          }
        } else {
          // No designs available for this category
          setSelectedKit(null);
          setSvgLoading(false);
        }
      } catch (error) {
        console.error(`Error fetching ${actualCategory} designs:`, error);
        // Set empty designs array if API fails - user will see "No designs available"
        setKitDesigns([]);
        setSelectedKit(null);
        setFrontSvgData(null);
        setBackSvgData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchKitDesigns();
    
    // Reset selected kit and SVG data when category changes
    return () => {
      setSelectedKit(null);
      setFrontSvgData(null);
      setBackSvgData(null);
      setSvgLoading(false);
    };
  }, [actualCategory]); // Refetch when category changes

  // Function to fetch SVG data
  const fetchSvgData = async (svgId, type) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "/api";
      const response = await axios.get(`${apiUrl}/media/${svgId}`);
      
      if (response.data && response.data.length > 0) {
        const mediaData = response.data[0];
        const svgUrl = publicUploadUrl(mediaData.filename);
        
        // Fetch the actual SVG content as text
        const svgResponse = await axios.get(svgUrl, {
          responseType: 'text',
          headers: {
            'Content-Type': 'text/plain'
          }
        });
        const svgContent = svgResponse.data;
        return svgContent;
      }
    } catch (error) {
      console.error(`Error fetching ${type} SVG:`, error);
    }
    return null;
  };

  // Event handlers
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleKitSelect = async (kitId) => {
    setSelectedKit(kitId);
    
    // Find the selected kit data
    const selectedKitData = kitDesigns.find(kit => kit.id === kitId);
    
    if (selectedKitData) {
      setSelectedKitPrice(parseFloat(selectedKitData.price) || 29.99); // Keep for backward compatibility
      setSelectedYoungKitPrice(parseFloat(selectedKitData.youngKitPrice) || parseFloat(selectedKitData.price) || 29.99);
      setSelectedAdultKitPrice(parseFloat(selectedKitData.adultKitPrice) || parseFloat(selectedKitData.price) || 29.99);
      setSvgLoading(true);
      
      // Fetch front and back SVG data
      const frontSvg = selectedKitData.frontSvgId ? await fetchSvgData(selectedKitData.frontSvgId, 'front') : null;
      const backSvg = selectedKitData.backSvgId ? await fetchSvgData(selectedKitData.backSvgId, 'back') : null;
      
      setFrontSvgData(frontSvg);
      setBackSvgData(backSvg);
      setSvgLoading(false);
    }
  };


  const handleColorChange = (colorType, colorValue) => {
    setSelectedColors(prev => ({
      ...prev,
      [colorType]: colorValue
    }));
  };

  const switchView = () => {
    setCurrentView(currentView === 'front' ? 'back' : 'front');
  };

  // Modal handlers

  const openColorModal = (colorType) => {
    setActiveColorPicker(colorType);
    setColorModalOpen(true);
  };
  const closeColorModal = () => setColorModalOpen(false);

  const openOrderModal = () => setOrderModalOpen(true);
  const closeOrderModal = () => setOrderModalOpen(false);
  
  // Bottom sheet handlers
  const openBottomSheet = (type) => {
    setActiveBottomSheet(type);
    setBottomSheetOpen(true);
    // Set default tab based on section type
    if (type === 'colors') {
      setActiveColorTab('color1');
    } else if (type === 'logo') {
      setActiveColorTab('logoColor');
    }
  };
  const closeBottomSheet = () => {
    setBottomSheetOpen(false);
    setActiveBottomSheet(null);
  };

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: 'calc(100vh - 200px)',
      display: 'flex', 
      flexDirection: 'column',
      position: 'relative',
      backgroundColor: 'transparent'
    }}>
      {/* Large Preview Area with Background */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        py: 4
      }}>
        {/* Background Layer */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'transparent',
          zIndex: 0
        }} />
        
        {/* Kit Preview Container - Centered */}
        <Box sx={{ 
          width: '100%', 
          maxWidth: '1200px',
          position: 'relative',
          zIndex: 1
        }}>
          <KitPreview 
            selectedKit={selectedKit}
            selectedColors={selectedColors}
            currentView={currentView}
            frontSvgData={frontSvgData}
            backSvgData={backSvgData}
            svgLoading={svgLoading}
            category={actualCategory}
          />
        </Box>
      </Box>

      {/* Bottom Control Bar - All Screen Sizes */}
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#2d2d2d',
        borderTop: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.28)',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        py: { xs: 1.5, md: 2 },
        px: { xs: 2, md: 4 }
      }}>
            {/* Design Selection */}
            <IconButton 
              onClick={() => openBottomSheet('design')}
              sx={{ 
                flexDirection: 'column',
                color: activeBottomSheet === 'design' ? '#0071e3' : 'rgba(255,255,255,0.88)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' }
              }}
            >
              <Image sx={{ fontSize: { xs: 28, md: 32 } }} />
              <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, mt: 0.5 }}>
                Design
              </Typography>
            </IconButton>

          {/* Colors */}
          <IconButton 
            onClick={() => openBottomSheet('colors')}
            sx={{ 
              flexDirection: 'column',
              color: activeBottomSheet === 'colors' ? '#0071e3' : 'rgba(255,255,255,0.88)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' }
            }}
          >
            <Palette sx={{ fontSize: { xs: 28, md: 32 } }} />
            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, mt: 0.5 }}>
              Colors
            </Typography>
          </IconButton>

          {/* Logo & Number */}
          <IconButton 
            onClick={() => openBottomSheet('logo')}
            sx={{ 
              flexDirection: 'column',
              color: activeBottomSheet === 'logo' ? '#0071e3' : 'rgba(255,255,255,0.88)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' }
            }}
          >
            <Badge sx={{ fontSize: { xs: 28, md: 32 } }} />
            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, mt: 0.5 }}>
              Logo #
            </Typography>
          </IconButton>

          {/* Order/Submit */}
          <IconButton 
            onClick={() => openBottomSheet('order')}
            sx={{ 
              flexDirection: 'column',
              color: activeBottomSheet === 'order' ? '#0071e3' : 'rgba(255,255,255,0.88)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' }
            }}
          >
            <ShoppingCart sx={{ fontSize: { xs: 28, md: 32 } }} />
            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, mt: 0.5 }}>
              Order
            </Typography>
          </IconButton>
        </Box>

        {/* Bottom Sheet Drawers */}
          <Drawer
            anchor="bottom"
            open={bottomSheetOpen}
            onClose={closeBottomSheet}
            ModalProps={{
              BackdropProps: {
                sx: {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  backdropFilter: 'blur(1px)'
                }
              }
            }}
            PaperProps={{
              sx: {
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                maxHeight: { xs: '80vh', md: '85vh' },
                maxWidth: { xs: '100%', md: '800px' },
                margin: { xs: '0 auto', md: '0 auto' },
                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                backdropFilter: 'blur(25px) saturate(180%)',
                WebkitBackdropFilter: 'blur(25px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderTop: '2px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.08)',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.3) 100%)'
              }
            }}
          >
            <Box sx={{ 
              p: { xs: 3, md: 4 }, 
              maxHeight: { xs: '80vh', md: '85vh' }, 
              overflowY: 'auto',
              backgroundColor: 'transparent'
            }}>
              {/* Close Button */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(15px)',
                borderRadius: 2,
                p: 1.5,
                px: 2
              }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000' }}>
                  {activeBottomSheet === 'design' && 'Choose Design'}
                  {activeBottomSheet === 'colors' && 'Choose Colors'}
                  {activeBottomSheet === 'logo' && 'Logo & Number'}
                  {activeBottomSheet === 'order' && 'Place Order'}
                </Typography>
                <IconButton onClick={closeBottomSheet} size="small">
                  <Close />
                </IconButton>
              </Box>

              {/* Design Selection Sheet */}
              {activeBottomSheet === 'design' && (
                <Box>
                  {loading ? (
                    <Typography sx={{ textAlign: 'center', py: 4 }}>
                      Loading designs...
                    </Typography>
                  ) : kitDesigns.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                        No designs available for {actualCategory}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please check back later or contact us for custom designs.
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={1.5}>
                      {kitDesigns.map((kit) => (
                        <Grid item xs={4} sm={3} md={2} key={kit.id}>
                          <Box
                            onClick={() => {
                              handleKitSelect(kit.id);
                              closeBottomSheet();
                            }}
                            sx={{
                              border: selectedKit === kit.id ? '3px solid #0071e3' : '1px solid rgba(0, 0, 0, 0.2)',
                              borderRadius: 1.5,
                              p: 0.5,
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              backgroundColor: 'rgba(255, 255, 255, 0.3)',
                              backdropFilter: 'blur(15px)',
                              '&:active': { 
                                transform: 'scale(0.95)'
                              }
                            }}
                          >
                            <img
                              src={kit.image}
                              alt={kit.name}
                              style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                                borderRadius: 4
                              }}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              )}

              {/* Colors Selection Sheet */}
              {activeBottomSheet === 'colors' && (
                <Box>
                  {/* Color Tabs */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                    {['color1', 'color2', 'color3'].map((colorKey) => (
                      <Button
                        key={colorKey}
                        variant={activeColorTab === colorKey ? 'contained' : 'outlined'}
                        onClick={() => setActiveColorTab(colorKey)}
                        sx={{
                          backgroundColor: activeColorTab === colorKey ? 'rgba(0, 113, 227, 0.9)' : 'rgba(255, 255, 255, 0.3)',
                          backdropFilter: 'blur(15px)',
                          color: activeColorTab === colorKey ? '#fff' : '#000',
                          border: '2px solid #000',
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 'bold',
                          px: 2,
                          py: 1,
                          '&:hover': {
                            backgroundColor: activeColorTab === colorKey ? 'rgba(0, 113, 227, 0.9)' : 'rgba(255, 255, 255, 0.5)'
                          }
                        }}
                      >
                        {colorKey === 'color1' ? 'Color 1' : colorKey === 'color2' ? 'Color 2' : 'Color 3'}
                      </Button>
                    ))}
                  </Box>

                  {/* Selected Color Display */}
                  <Box sx={{ 
                    border: '1px solid rgba(0, 0, 0, 0.15)', 
                    borderRadius: 2, 
                    p: 2, 
                    mb: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    backdropFilter: 'blur(15px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      border: '2px solid #000',
                      borderRadius: 2,
                      backgroundColor: selectedColors[activeColorTab] || '#fff'
                    }} />
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {getColorName(selectedColors[activeColorTab])}
                    </Typography>
                  </Box>

                  {/* Color Grid */}
                  <Grid container spacing={1}>
                    {KIT_COLOR_SWATCHES.map((color) => (
                      <Grid item xs={2} sm={1.5} key={color.value}>
                        <Box
                          onClick={() => handleColorChange(activeColorTab, color.value)}
                          sx={{
                            aspectRatio: '1',
                            maxWidth: { xs: 50, md: 45 },
                            maxHeight: { xs: 50, md: 45 },
                            backgroundColor: color.value,
                            border: normalizeHex(selectedColors[activeColorTab]) === color.value ? '2px solid #0071e3' : '1px solid #e0e0e0',
                            borderRadius: 1.5,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            '&:active': {
                              transform: 'scale(0.9)'
                            }
                          }}
                        >
                          {normalizeHex(selectedColors[activeColorTab]) === color.value && (
                            <CheckCircle sx={{ color: isLightSwatchHex(color.value) ? '#000' : '#fff', fontSize: { xs: 18, md: 16 } }} />
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Logo & Number Sheet */}
              {activeBottomSheet === 'logo' && (
                <Box>
                  {/* Logo/Number Tabs */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    <Button
                      fullWidth
                      variant={activeColorTab === 'logoColor' ? 'contained' : 'outlined'}
                      onClick={() => setActiveColorTab('logoColor')}
                      sx={{
                        backgroundColor: activeColorTab === 'logoColor' ? 'rgba(0, 113, 227, 0.9)' : 'rgba(255, 255, 255, 0.3)',
                        backdropFilter: 'blur(15px)',
                        color: activeColorTab === 'logoColor' ? '#fff' : '#000',
                        border: '2px solid #000',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: activeColorTab === 'logoColor' ? 'rgba(0, 113, 227, 0.9)' : 'rgba(255, 255, 255, 0.5)'
                        }
                      }}
                    >
                      Logo Color
                    </Button>
                    <Button
                      fullWidth
                      variant={activeColorTab === 'numberColor' ? 'contained' : 'outlined'}
                      onClick={() => setActiveColorTab('numberColor')}
                      sx={{
                        backgroundColor: activeColorTab === 'numberColor' ? 'rgba(0, 113, 227, 0.9)' : 'rgba(255, 255, 255, 0.3)',
                        backdropFilter: 'blur(15px)',
                        color: activeColorTab === 'numberColor' ? '#fff' : '#000',
                        border: '2px solid #000',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: activeColorTab === 'numberColor' ? 'rgba(0, 113, 227, 0.9)' : 'rgba(255, 255, 255, 0.5)'
                        }
                      }}
                    >
                      Number Color
                    </Button>
                  </Box>

                  {/* Selected Color Display */}
                  <Box sx={{ 
                    border: '1px solid rgba(0, 0, 0, 0.15)', 
                    borderRadius: 2, 
                    p: 2, 
                    mb: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    backdropFilter: 'blur(15px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      border: '2px solid #000',
                      borderRadius: 2,
                      backgroundColor: selectedColors[activeColorTab] || '#fff'
                    }} />
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {getColorName(selectedColors[activeColorTab])}
                    </Typography>
                  </Box>

                  {/* Color Grid */}
                  <Grid container spacing={1}>
                    {KIT_COLOR_SWATCHES.map((color) => (
                      <Grid item xs={2} sm={1.5} key={color.value}>
                        <Box
                          onClick={() => handleColorChange(activeColorTab, color.value)}
                          sx={{
                            aspectRatio: '1',
                            maxWidth: { xs: 50, md: 45 },
                            maxHeight: { xs: 50, md: 45 },
                            backgroundColor: color.value,
                            border: normalizeHex(selectedColors[activeColorTab]) === color.value ? '2px solid #0071e3' : '1px solid #e0e0e0',
                            borderRadius: 1.5,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            '&:active': {
                              transform: 'scale(0.9)'
                            }
                          }}
                        >
                          {normalizeHex(selectedColors[activeColorTab]) === color.value && (
                            <CheckCircle sx={{ color: isLightSwatchHex(color.value) ? '#000' : '#fff', fontSize: { xs: 18, md: 16 } }} />
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Order Form Sheet */}
              {activeBottomSheet === 'order' && (
                <Box>
                  <Box component="form">
                    {/* Name */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                        Name <span style={{ color: '#0071e3' }}>*</span>
                      </Typography>
                      <Box
                        component="input"
                        type="text"
                        placeholder="Your Name"
                        value={orderFormData.name}
                        onChange={(e) => handleFormChange('name', e.target.value)}
                        sx={{
                          width: '100%',
                          p: 1.5,
                          border: formErrors.name ? '2px solid #0071e3' : '1px solid rgba(0, 0, 0, 0.15)',
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.4)',
                          backdropFilter: 'blur(15px)',
                          fontSize: '16px',
                          '&:focus': {
                            outline: 'none',
                            borderColor: '#0071e3',
                            backgroundColor: 'rgba(255, 255, 255, 0.6)'
                          }
                        }}
                      />
                      {formErrors.name && (
                        <Typography variant="caption" sx={{ color: '#0071e3', mt: 0.5, display: 'block' }}>
                          {formErrors.name}
                        </Typography>
                      )}
                    </Box>

                    {/* Email */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                        Email <span style={{ color: '#0071e3' }}>*</span>
                      </Typography>
                      <Box
                        component="input"
                        type="email"
                        placeholder="your@email.com"
                        value={orderFormData.email}
                        onChange={(e) => handleFormChange('email', e.target.value)}
                        sx={{
                          width: '100%',
                          p: 1.5,
                          border: formErrors.email ? '2px solid #0071e3' : '1px solid rgba(0, 0, 0, 0.15)',
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.4)',
                          backdropFilter: 'blur(15px)',
                          fontSize: '16px',
                          '&:focus': {
                            outline: 'none',
                            borderColor: '#0071e3',
                            backgroundColor: 'rgba(255, 255, 255, 0.6)'
                          }
                        }}
                      />
                      {formErrors.email && (
                        <Typography variant="caption" sx={{ color: '#0071e3', mt: 0.5, display: 'block' }}>
                          {formErrors.email}
                        </Typography>
                      )}
                    </Box>

                    {/* Phone */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                        Phone <span style={{ color: '#0071e3' }}>*</span>
                      </Typography>
                      <Box
                        component="input"
                        type="tel"
                        placeholder="+1234567890"
                        value={orderFormData.phone}
                        onChange={(e) => handleFormChange('phone', e.target.value)}
                        sx={{
                          width: '100%',
                          p: 1.5,
                          border: formErrors.phone ? '2px solid #0071e3' : '1px solid rgba(0, 0, 0, 0.15)',
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.4)',
                          backdropFilter: 'blur(15px)',
                          fontSize: '16px',
                          '&:focus': {
                            outline: 'none',
                            borderColor: '#0071e3',
                            backgroundColor: 'rgba(255, 255, 255, 0.6)'
                          }
                        }}
                      />
                      {formErrors.phone && (
                        <Typography variant="caption" sx={{ color: '#0071e3', mt: 0.5, display: 'block' }}>
                          {formErrors.phone}
                        </Typography>
                      )}
                    </Box>

                    {/* Team Name */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                        Team Name <span style={{ color: '#0071e3' }}>*</span>
                      </Typography>
                      <Box
                        component="input"
                        type="text"
                        placeholder="Team Name"
                        value={orderFormData.teamName}
                        onChange={(e) => handleFormChange('teamName', e.target.value)}
                        sx={{
                          width: '100%',
                          p: 1.5,
                          border: formErrors.teamName ? '2px solid #0071e3' : '1px solid rgba(0, 0, 0, 0.15)',
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.4)',
                          backdropFilter: 'blur(15px)',
                          fontSize: '16px',
                          '&:focus': {
                            outline: 'none',
                            borderColor: '#0071e3',
                            backgroundColor: 'rgba(255, 255, 255, 0.6)'
                          }
                        }}
                      />
                      {formErrors.teamName && (
                        <Typography variant="caption" sx={{ color: '#0071e3', mt: 0.5, display: 'block' }}>
                          {formErrors.teamName}
                        </Typography>
                      )}
                    </Box>

                    {/* Kit type */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                        Kit type <span style={{ color: '#0071e3' }}>*</span>
                      </Typography>
                      <Box
                        component="select"
                        value={orderFormData.kitType}
                        onChange={(e) => handleFormChange('kitType', e.target.value)}
                        sx={{
                          width: '100%',
                          p: 1.5,
                          border: '1px solid rgba(0, 0, 0, 0.15)',
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.4)',
                          backdropFilter: 'blur(15px)',
                          fontSize: '16px',
                          '&:focus': {
                            outline: 'none',
                            borderColor: '#0071e3',
                            backgroundColor: 'rgba(255, 255, 255, 0.6)'
                          }
                        }}
                      >
                        <option value="adult">Adult</option>
                        <option value="young">Young / Youth</option>
                      </Box>
                      {orderFormData.quantity && (
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#555' }}>
                          {(() => {
                            const p = calculateQuotePricing({
                              quantity: orderFormData.quantity,
                              youngKitPrice: selectedYoungKitPrice,
                              adultKitPrice: selectedAdultKitPrice,
                              kitType: orderFormData.kitType,
                              fallbackPrice: 29.99
                            });
                            return `Estimated total: ${formatMoney(p.total_amount)} (${orderFormData.quantity} × ${formatMoney(p.unit_price)} per kit)`;
                          })()}
                        </Typography>
                      )}
                    </Box>

                    {/* Quantity */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                        Number of Kits <span style={{ color: '#0071e3' }}>*</span>
                      </Typography>
                      <Box
                        component="select"
                        value={orderFormData.quantity}
                        onChange={(e) => handleFormChange('quantity', e.target.value)}
                        sx={{
                          width: '100%',
                          p: 1.5,
                          border: formErrors.quantity ? '2px solid #0071e3' : '1px solid rgba(0, 0, 0, 0.15)',
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.4)',
                          backdropFilter: 'blur(15px)',
                          fontSize: '16px',
                          '&:focus': {
                            outline: 'none',
                            borderColor: '#0071e3',
                            backgroundColor: 'rgba(255, 255, 255, 0.6)'
                          }
                        }}
                      >
                        <option value="">Select Quantity</option>
                        {Array.from({ length: 19 }, (_, i) => i + 12).map(num => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </Box>
                      {formErrors.quantity && (
                        <Typography variant="caption" sx={{ color: '#0071e3', mt: 0.5, display: 'block' }}>
                          {formErrors.quantity}
                        </Typography>
                      )}
                    </Box>

                    {/* Submit Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={async () => {
                        const success = await handleFormSubmit();
                        // Only close the bottom sheet if submission was successful
                        // (On success, user will be navigated away anyway, but this prevents closing on validation failure)
                        if (success) {
                          closeBottomSheet();
                        }
                      }}
                      disabled={isSubmitting}
                      sx={{
                        backgroundColor: 'rgba(0, 113, 227, 0.9)',
                        backdropFilter: 'blur(10px)',
                        color: '#fff',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        py: 1.2,
                        fontSize: '16px',
                        mt: 1,
                        border: '1px solid rgba(0, 113, 227, 0.5)',
                        '&:hover': {
                          backgroundColor: '#0077ed'
                        },
                        '&:disabled': {
                          backgroundColor: 'rgba(204, 204, 204, 0.7)'
                        }
                      }}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Order'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Drawer>

      {/* Modals */}

      <ColorPickerModal 
        open={colorModalOpen} 
        onClose={closeColorModal}
        activeColorPicker={activeColorPicker}
        selectedColors={selectedColors}
        onColorSelect={handleColorChange}
      />

      <OrderFormModal 
        open={orderModalOpen} 
        onClose={closeOrderModal}
        selectedKit={selectedKit}
        selectedColors={selectedColors}
        kitDesigns={kitDesigns}
      />

      {/* Submission Loading Backdrop */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(4px)'
        }}
        open={isSubmitting}
      >
        <Box sx={{ 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3
        }}>
          <CircularProgress 
            size={60} 
            thickness={4}
            sx={{ 
              color: '#0071e3',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round'
              }
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#fff',
              fontWeight: 'bold',
              maxWidth: 400,
              px: 2
            }}
          >
            {submissionStatus || 'Processing your order...'}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              maxWidth: 350,
              px: 2
            }}
          >
            Please wait, this may take a moment...
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default FootballDesign;